import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, UserCheck, Search } from "lucide-react";

export function QuoteRecipient({ quoteNumber, open, emailTo, setEmailTo }) {
  const [rec, setRec] = useState(undefined);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);

  useEffect(() => {
    if (!open) return;
    setRec(undefined); setQ(""); setHits([]);
    api.get(`/quotes/${quoteNumber}/recipient`).then((r) => {
      setRec(r.data.recipient);
      if (r.data.recipient?.email) setEmailTo(r.data.recipient.email);
    }).catch(() => setRec(null));
  }, [open, quoteNumber, setEmailTo]);

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(() => api.get("/quotes/meta/customers", { params: { q } }).then((r) => setHits(r.data.items || [])).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (c) => { setRec({ ...c, source: "search" }); setEmailTo(c.email || ""); setQ(""); setHits([]); };

  return (
    <div className="space-y-2">
      {rec === undefined ? (
        <p className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Finding customer…</p>
      ) : rec ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="quote-recipient-found">
          <UserCheck className="w-4 h-4" />
          <span><b>{rec.full_name}</b>{rec.company ? ` · ${rec.company}` : ""} {rec.source === "job_customer" ? "(from linked job)" : rec.source === "search" ? "(selected)" : "(quote customer)"}{!rec.email && " · no email on file"}</span>
        </div>
      ) : (
        <p className="text-xs text-amber-600" data-testid="quote-recipient-none">No customer found for this quote — search below or type an email.</p>
      )}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers by name, company or email…" className="pl-8" data-testid="quote-recipient-search" />
        {hits.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto">
            {hits.map((c) => (
              <button key={c.legacy_id} type="button" onClick={() => pick(c)} data-testid={`quote-recipient-hit-${c.legacy_id}`}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-indigo-50">
                <span className="font-medium text-slate-800">{c.full_name}</span>
                <span className="ml-2 text-xs text-slate-500">{c.email || "no email"}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Client email</label>
        <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="client@example.com" className="mt-1" data-testid="quote-email-to" />
      </div>
    </div>
  );
}
